/**
 * A token type to describe URL parsed result
 * it can only a a general pattern or an Url Parameter
 *
 * ```
 * /foo/:bar
 *  \_/ \_/
 *   |   |
 *   |   Param
 *   |
 * Pattern
 * ```
 */
export var TokenType;
(function (TokenType) {
    /**
     * A general pattern token
     */
    TokenType["Pattern"] = "PATTERN";
    /**
     * An Url parameter token
     */
    TokenType["Param"] = "PARAM";
})(TokenType || (TokenType = {}));
const { Pattern, Param } = TokenType;
/**
 * A function to translate tokens into Regex for Url matching
 *
 * @param tokens - List of parsed tokens
 * @param options - {@link TokensToRegexOption}
 *
 * @returns A string representation of the regex
 */
export const tokensToRegexPattern = (tokens, options = {}) => {
    const { deliminator = '/', encode = (x) => x } = options;
    const segments = tokens.map(token => token.type === Pattern ? encode(token.value) : '([^/]+)');
    return ['^', ...segments].join(deliminator);
};
/**
 * @internal
 */
const createPatternToken = (value) => ({
    type: Pattern,
    value,
});
/**
 * @internal
 */
const createParamToken = (value) => ({
    type: Param,
    value,
});
/**
 * A parser to parse provided path and turn it into a list of tokens
 *
 * @param path - Path for the route
 * @param options - See {@link ParseOptions}
 *
 * @returns A list of {@link Token}
 */
export const parse = (path, options = {}) => {
    const { pathDeliminator = '/', paramDeliminator = ':', groupDeliminatorStart = '(', groupDeliminatorEnd = ')', urlParamPattern = '[a-zA-Z][a-zA-Z0-9_]*', } = options;
    const pathSegments = path.slice(1).split(pathDeliminator);
    const urlParamRegex = new RegExp(['^', paramDeliminator, urlParamPattern, '$'].join(''));
    return pathSegments.map(segment => {
        const hasGroup = segment.includes(groupDeliminatorStart);
        const hasParam = segment.includes(paramDeliminator);
        if (hasParam && !urlParamRegex.test(segment)) {
            throw new Error('Invalid URL Parameters defined');
        }
        if (hasGroup && ['?', '\\'].some(p => segment.includes(p))) {
            throw new Error('Invalid Group URL defined');
        }
        const pattern = hasGroup
            ? segment
                .replace(groupDeliminatorStart, '(?:')
                .replace(groupDeliminatorEnd, ')')
            : segment;
        return hasParam
            ? createParamToken(segment.slice(1))
            : createPatternToken(pattern);
    });
};
/**
 * Create a path matcher for provided `pathname`
 *
 * @param pathname - The pathname from `location.pathname`
 *
 * @returns A path matcher to match predefined route path
 *
 * @example
 *
 * ```typescript
 * const matchPath = createPathMatch('/foo/1234');
 * const {matched, params, path, pathname} = matchPath('/foo/:bar');
 *
 * expect(matched).toBeTruthy();
 * expect(params).toEqual({bar: '1234'});
 * expect(path).toEqual('/foo/:bar');
 * expect(pathname).toEqual('/foo/1234');
 * ```
 */
export const createPathMatch = (pathname) => (path, options = {}) => {
    const { exact = false } = options;
    const tokens = parse(path);
    const paramTokens = tokens.filter(token => token.type === Param);
    const pathPattern = [
        tokensToRegexPattern(tokens),
        exact ? '$' : '',
    ].join('');
    const pathRegex = new RegExp(pathPattern);
    const result = pathRegex.exec(pathname);
    const params = paramTokens.length && result?.length
        ? Object.fromEntries(paramTokens.map((token, idx) => {
            const param = result[idx + 1];
            return [
                token.value,
                param ? decodeURIComponent(param) : param,
            ];
        }))
        : {};
    const matched = Boolean(result);
    return { params, matched, path: pathPattern, pathname };
};
/**
 * Utility function to remove duplicated slashes
 *
 * @param path - the path to be used for the removing
 */
export const dedupSlash = (path) => path.replace(/\/{2,}/, '/');
/**
 * Utility function to remove trailing slash if it is not at the beginning
 *
 * @param path - the path to be used for the removing
 */
export const deTrailingSlash = (path) => path.replace(/(.+)\/$/, '$1');
/**
 * Utility function to join provided `path` with the `base`
 *
 * @param path - the path to join with base
 * @param base - base path
 */
export const joinPath = (path, base) => deTrailingSlash(dedupSlash([base, path].join('/')));
/**
 * Url builder
 *
 * @param href - A link ar a path
 * @param options - @see BuildUrlOptions
 * @returns Url {@link Url}
 */
export const buildUrl = (href, { state, title = document.title, base = '', location = window.location, } = {}) => {
    // Build a URL relative to the current location.href
    const url = new URL(href, location.href);
    if (base) {
        url.pathname = joinPath(url.pathname, base);
    }
    const urlProps = {
        // Remove ":" from URL.protocol
        protocol: url.protocol.slice(0, -1),
        host: url.hostname,
        // Convert the port into number if there is port
        port: url.port ? Number.parseInt(url.port, 10) : undefined,
        // Non-special schemes, e.g. `tauri://localhost`, parse to an empty path
        path: url.pathname || '/',
        // Remove "?" from URL.search
        query: url.search.slice(1),
        // Remove "#" from URL.hash
        fragment: url.hash.slice(1),
        state: { title, ...state },
    };
    const port = urlProps.port ? `:${urlProps.port}` : '';
    const toOrigin = () => `${urlProps.protocol}://${urlProps.host}${port}`;
    const isInternal = () => location.origin === toOrigin();
    const isExternal = () => !isInternal();
    const toString = () => url.href;
    const query = urlProps.query ? `?${urlProps.query}` : urlProps.query;
    const fragment = urlProps.fragment
        ? `#${urlProps.fragment}`
        : urlProps.fragment;
    const toInternalHref = () => [urlProps.path, query, fragment].join('');
    return {
        ...urlProps,
        toOrigin,
        isInternal,
        isExternal,
        toString,
        toInternalHref,
    };
};
export const isUrl = (url) => !!(typeof url === 'object' && url && 'host' in url);
export const toUrl = (url, options) => isUrl(url) ? url : buildUrl(url, options);
/**
 * Check if the provided url is internal
 *
 * @param url - Url to check w/r/t `location.origin`
 *
 * @returns `true` if it is internal url
 */
export const isInternalUrl = (url) => {
    return toUrl(url).isInternal();
};
//# sourceMappingURL=index.js.map